import { Heart, MessageCircle, Share2, MoreHorizontal } from "lucide-react";
import { Button, Image } from "@city-sage/aurum-avis-labs-components";
import { Post } from "@city-sage/core-client";
import { formatDistanceToNow, parseISO } from "date-fns";
import { Link } from "@tanstack/react-router";
import { Avatar } from "../Avatar";
import { useReactions } from "../../lib/useReactions";

interface PostCardProps {
  post: Post;
  onLike?: (postId: string) => void;
  onComment?: (postId: string) => void;
} 

export function PostCard({ post, onLike, onComment }: PostCardProps) {
  const { isLiked, likeCount, toggleLike, isPending } = useReactions({
    reactableType: 'post',
    reactableId: post.id
  });

  const handleLike = () => {
    toggleLike();
  };

  // Ensure we're working with the actual current time vs post creation time
  const timeAgo = formatDistanceToNow(post.createdAt, { addSuffix: true });

  return (
    <div className="bg-white border-b border-gray-100 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <Link 
          to="/users/$userId" 
          params={{ userId: post.account.id }}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <Avatar className="h-8 w-8" account={post.account} />
          <div>
            <p className="font-medium text-sm">{post.account?.userName || 'Anonymous'}</p>
            <p className="text-xs text-gray-500">{timeAgo}</p>
          </div>
        </Link>
      </div>

      {/* Content */}
      {post.text && (
        <p className="text-gray-800 mb-3 leading-relaxed">{post.text}</p>
      )}

      {/* Image */}
      {post.imageUrl && (
        <div className="mb-3 rounded-lg overflow-hidden">
          <Image
            src={post.imageUrl}
            alt="Post image"
            className="w-full h-auto max-h-96 object-cover"
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-6 pt-2">
        <Button 
          variant="ghost" 
          onClick={handleLike}
          disabled={isPending}
          className="h-8 px-2 flex items-center gap-1 text-gray-600 hover:text-red-500"
        >
          <Heart className={`h-4 w-4 ${isLiked ? "fill-red-500 text-red-500" : ""}`} />
          <span className="text-xs">
            {likeCount > 0 ? `${likeCount}` : 'Like'}
          </span>
        </Button>
        
      </div>
    </div>
  );
} 